import { isArray } from 'lodash';
import { useState } from 'react';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { TextInput } from '@hivebrite/design-system/Inputs';
import { FakeLink } from '@hivebrite/design-system/Link';
import { toast } from '@hivebrite/design-system/Toast';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import Loader from 'hb-react/publicoffice/apps/LoginApp/components/Loader';
import { postUserSession } from 'hb-react/publicoffice/apps/LoginApp/requests';
import {
  clearAuthAttempt,
  METHODS,
  setAuthAttempt,
} from 'hb-react/publicoffice/apps/LoginApp/utils/authAttemptSessionStorage';
import CustomContentBox from 'hb-react/publicoffice/apps/LoginApp/utils/CustomContentBox';
import { HtmlContent } from 'hb-react/shared/components/HtmlContent';

interface LoginMFAProps {
  displayMFA: {
    network_id: number;
    redirect_to: string | undefined;
    user: {
      email: string;
      network_id: number;
      password: string;
      remember_me: number;
      timezone: string;
    };
  };
}

const ResendCodeFakeLink = ({
  handleResendCode,
}: {
  handleResendCode: () => void;
}) => (
  <FakeLink onClick={() => handleResendCode()} mx={1}>
    {s__('LoginMFA|Resend a new code')}
  </FakeLink>
);

const LoginMFA = ({ displayMFA }: LoginMFAProps) => {
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const saveButtonShouldBeDisabled = !code || code.length < 6 || !!error;
  const { currentNetwork } = useDefaultAppContexts();

  if (!currentNetwork) {
    return <Loader />;
  }

  const adminNotificationEmails = isArray(
    currentNetwork.admin_notification_emails,
  )
    ? currentNetwork.admin_notification_emails.join(',')
    : currentNetwork.admin_notification_emails;

  const generateContactLink = () => ({
    url: `mailto:${adminNotificationEmails}?subject=${s__(
      'LoginMFA|Need assistance with two factor authentication',
    )}`,
    label: __('Contact support'),
    iconName: 'envelope',
  });

  const handleSubmit = async () => {
    setAuthAttempt(METHODS.MFA);
    setIsLoading(true);

    const valuesToSubmit = {
      ...displayMFA,
      user: {
        ...displayMFA.user,
        otp_attempt: code,
      },
    };

    try {
      await postUserSession(valuesToSubmit);
    } catch (e) {
      clearAuthAttempt();
      if (e?.response?.data?.error === I18n.t('devise.failure.invalid')) {
        setError(
          s__('LoginMFA|The verification code you entered is incorrect'),
        );
        setIsLoading(false);
      }

      toast.error(e?.response?.data?.error || __('An error has occurred'));
    }

    setIsLoading(false);
  };

  const handleResendCode = async () => {
    setIsLoading(true);

    try {
      await postUserSession(displayMFA);
    } catch (e) {
      if (e?.response?.data?.error === 'OTP_REQUIRED') {
        toast.success(__('Code successfully sent'));
      } else {
        toast.error(e?.response?.data?.error || __('An error has occurred'));
      }
    }

    setIsLoading(false);
  };

  return (
    <Box flexDirection="column">
      <Box font="h3-semibold" mb={3}>
        {s__('LoginMFA|Two-factor authentication')}
      </Box>
      <Box>
        {s__("LoginMFA|We've sent you an email with a verification code.")}
      </Box>
      <Box mb={6}>
        {s__(
          'LoginMFA|Please enter the code below. This code will be valid for 5 minutes',
        )}
      </Box>
      <TextInput
        id="mfa-code"
        name="mfa-code"
        label={s__('LoginMFA|Verification code')}
        placeholder={s__('LoginMFA|Enter one or more characters')}
        value={code}
        onChange={(e) => {
          setError(undefined);
          setCode(e.target.value);
        }}
        required
      />
      {error && (
        <AdditionalInformation id="code-error" type="error">
          {error}
        </AdditionalInformation>
      )}
      <CustomContentBox mt={4}>
        <HtmlContent content={s__('LoginMFA|Have not received it ?')} />
        <ResendCodeFakeLink handleResendCode={handleResendCode} />
        {s__('LoginMFA|or')}
        <FakeLink
          mx={1}
          onClick={() => {
            window.open(generateContactLink().url, '_blank');
          }}
        >
          {s__('LoginMFA|Contact support')}
        </FakeLink>
      </CustomContentBox>
      <Box alignSelf="end" mt={6}>
        <Button
          isDisabled={saveButtonShouldBeDisabled}
          onClick={() => handleSubmit()}
          isLoading={isLoading}
        >
          {s__('LoginMFA|Continue')}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginMFA;
